import { assertUnreachable } from '@hover/utilities';
import type { RefCallback, RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

export type ScrollDirection = 'left' | 'right' | 'up' | 'down';

export interface UseScrollToOptions {
  /**
   * Percentage of the containers visible width or height to scroll by on each
   * invocation of `scroll`
   */
  step?: number;
  /**
   * A `RefObject` for the container to scroll.
   *
   * @example
   * ```tsx
   * const ref = useRef<HTMLDivElement>(null);
   * const [scroll] = useScrollTo({ ref });
   *
   * <div ref={ref} />
   * <button onClick={() => scroll('left')} />
   * ```
   *
   * ⚠️ NOTE: if you need to detect when the container is scrolled all the way in a
   * given direction (`atTop`, `atLeft`, etc.), you must instead use the `ref`
   * returned with that state object
   *
   * @example
   * ```tsx
   * const [scroll, { ref, atLeft }] = useScrollTo();
   *
   * <div ref={ref} />
   * <button disabled={atLeft} onClick={() => scroll('left')} />
   * ```
   */
  ref?: RefObject<HTMLElement>;
}

/**
 * A hook that provides a `scroll` function for scrolling a container in a given
 * direction by the provided `step` percentage using the `element.scrollTo` API.
 *
 * @param options hook options, see `UseScrollToOptions` for details
 * @see {@link UseScrollToOptions}
 *
 * @returns tuple containing the `scroll` function and an object containing the
 * `ref` and constraints (`atTop`, `atLeft`, etc.) state for detecting when the
 * container is scrolled all the way in each direction
 */
const useScrollTo = ({ step = 0.5, ref: refProp }: UseScrollToOptions = {}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  const ref: RefCallback<HTMLElement> = useCallback(element => {
    if (!element) return;

    setContainer(element);
  }, []);

  const getContainer = useCallback(
    () => refProp?.current ?? container,
    [container, refProp],
  );

  const scroll = useCallback(
    (direction: ScrollDirection) => {
      const element = getContainer();

      if (!element) return;

      const distance =
        direction === 'left' || direction === 'right'
          ? element.clientWidth * step
          : element.clientHeight * step;

      const to = ((): ScrollToOptions => {
        switch (direction) {
          case 'left':
            return { left: element.scrollLeft - distance };
          case 'right':
            return { left: element.scrollLeft + distance };
          case 'up':
            return { top: element.scrollTop - distance };
          case 'down':
            return { top: element.scrollTop + distance };
          default:
            return assertUnreachable(direction);
        }
      })();

      element.scrollTo({ behavior: 'smooth', ...to });
    },
    [getContainer, step],
  );

  const [atTop, setAtTop] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const [atLeft, setAtLeft] = useState(false);
  const [atRight, setAtRight] = useState(false);

  const updateConstraints = useCallback((element: HTMLElement) => {
    setAtTop(element.scrollTop === 0);
    setAtLeft(element.scrollLeft === 0);
    setAtRight(
      element.scrollLeft + element.clientWidth === element.scrollWidth,
    );
    setAtBottom(
      element.scrollTop + element.clientHeight === element.scrollHeight,
    );
  }, []);

  const onScroll = useCallback(
    (event: Event) => {
      const element = event.currentTarget;

      if (!(element instanceof HTMLElement)) return;

      updateConstraints(element);
    },
    [updateConstraints],
  );

  useEffect(() => {
    if (!container) return undefined;

    container.addEventListener('scroll', onScroll);

    updateConstraints(container);

    return () => container.removeEventListener('scroll', onScroll);
  }, [container, onScroll, updateConstraints]);

  return [scroll, { ref, atTop, atBottom, atLeft, atRight }] as const;
};

export { useScrollTo };
